import type { CheckoutFormValuesType } from '@ecomm/checkout/shipping-schema'
import { isStrategicReferralPartner } from '@ecomm/checkout/utils'
import { useCartCheckout, useCartCustomFields } from '@ecomm/data-cart'
import { useIsD2DCart } from '@ecomm/data-cart/getters/useIsD2DCart'
import { getStringCustomField } from '@ecomm/data-cart/utils/getStringCustomField'
import { useLocale } from '@ecomm/data-hooks'
import type { CheckoutValue } from '@ecomm/data-simplisafe-api'
import {
  setBrazeAttributeGoogleClientId,
  useGoogleAnalytics,
  useTrackingCheckoutFailure,
  useTrackingCheckoutSubmit
} from '@ecomm/tracking'
import { noValue, set as sessionStorageSet } from '@ecomm/utils'
import type { Address } from '@simplisafe/eis-commercetools-carts'
import * as O from 'fp-ts/Option'
import { navigate } from 'gatsby'
import { useCallback } from 'react'
import { match } from 'ts-pattern'

import { getValueFromPartnerCookie } from '@ecomm/shared-cookies'
import {
  combineCartAndSuggestedAddress,
  getFirstSuggestedAddress,
  isAddressValidationSupported,
  shouldRenderShippingAddressModal,
  shouldSilentlyUpdateAddress
} from './addressValidationHelpers'

export const useCheckoutSubmit = (
  redirectUrl: string,
  openAddressModal: VoidFunction
) => {
  const cartCheckout = useCartCheckout()
  const locale = useLocale()
  const [clientId] = useGoogleAnalytics()

  const trackCheckoutSubmit = useTrackingCheckoutSubmit()
  const trackCheckoutFailure = useTrackingCheckoutFailure()

  const isD2DCart = useIsD2DCart()
  const cartCustomFields = useCartCustomFields()

  /**
   * Called to correct the shipping address under the hood without opening the modal
   */
  const silentlyUpdateAddress = useCallback(
    (
      { cart, addressValidation }: CheckoutValue,
      sameBillingAddress: boolean
    ) => {
      // Submit existing values from cart + first suggested address
      cartCheckout(
        {
          leadOther: O.toUndefined(
            getStringCustomField(cart.custom, 'leadOther')
          ),
          leadSource: O.toUndefined(
            getStringCustomField(cart.custom, 'leadSource')
          ),
          sameBillingAddress,
          shippingAddress: combineCartAndSuggestedAddress(
            locale,
            cart.shippingAddress,
            getFirstSuggestedAddress(addressValidation)
          ),
          shippingMethod: cart.shippingMethod.id || '',
          shouldValidate: false
        },
        trackCheckoutFailure,
        () => navigate(redirectUrl)
      )
    },
    [cartCheckout, trackCheckoutFailure, locale, redirectUrl]
  )

  /**
   * Handles initial checkout response according to address validation results
   */
  const handleSuccess = useCallback(
    (response: CheckoutValue, sameBillingAddress: boolean) => {
      match(response.addressValidation)
        .when(
          validation => !validation,
          () => navigate(redirectUrl)
        )
        .when(
          validation => shouldRenderShippingAddressModal(locale, validation),
          () => openAddressModal()
        )
        .when(
          validation => shouldSilentlyUpdateAddress(locale, validation),
          () => silentlyUpdateAddress(response, sameBillingAddress)
        )
        .otherwise(() => navigate(redirectUrl))
    },
    [openAddressModal, silentlyUpdateAddress, locale, redirectUrl]
  )

  const onSubmit = useCallback(
    (values: CheckoutFormValuesType) => {
      // Used to prepopulate the checkboxes if user returns to the page
      sessionStorageSet('offerAndTip', JSON.stringify(values.offerAndTip))
      sessionStorageSet('sameAddress', JSON.stringify(values.sameAddress))

      trackCheckoutSubmit(values)
      // set google client id for UK as custom braze attribute ECP-12023
      locale === 'en-GB' &&
        clientId &&
        setBrazeAttributeGoogleClientId(clientId)

      const formShippingAddress: Address = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        country: locale === 'en-US' ? 'US' : 'GB',
        city: values.city,
        state: locale === 'en-US' && values.state ? values.state : noValue(),
        streetName: values.streetName,
        postalCode: values.postalCode,
        additionalStreetInfo: values.additionalStreetInfo || ''
      }

      const userIsStrategicReferral = isStrategicReferralPartner()
      const formLeadSource = userIsStrategicReferral
        ? 'Partner Strategic Referral'
        : isD2DCart
          ? 'doorToDoor'
          : values.foundInfoThrough

      const cartPartnerName = getStringCustomField(
        cartCustomFields,
        'partnerName'
      )

      const formLeadOther =
        (userIsStrategicReferral
          ? getValueFromPartnerCookie('partnerName')
          : isD2DCart
            ? O.toUndefined(cartPartnerName)
            : values.additionalFoundInfoThrough) || ''

      // Submit form values to checkout endpoint
      cartCheckout(
        {
          leadOther: formLeadOther,
          leadSource: formLeadSource,
          sameBillingAddress: values.sameAddress,
          shippingAddress: formShippingAddress,
          shippingMethod: values.shippingOption,
          shouldValidate: isAddressValidationSupported(locale)
        },
        trackCheckoutFailure,
        res => handleSuccess(res, values.sameAddress)
      )
    },
    [
      trackCheckoutSubmit,
      locale,
      cartCheckout,
      trackCheckoutFailure,
      handleSuccess,
      isD2DCart,
      cartCustomFields
    ]
  )

  return onSubmit
}
